/**
 * SolFire Curve Band Definitions
 * 
 * The pricing curve is defined by piecewise linear bands.
 * Each band has an end_tokens threshold, a start_price, and a slope.
 * 
 * Price calculation within a band:
 *   marginal_price = start_price + (tokens_in_band * slope)
 * 
 * Boosted price:
 *   effective_price = marginal_price + boost
 */

export const TOKEN_DECIMALS = 6;
export const TOKEN_SCALE = 1_000_000; // 10^6
export const LAMPORTS_PER_SOL = 1_000_000_000; // 10^9

/**
 * Curve bands define the piecewise linear pricing structure.
 * Prices and slopes are in lamports.
 * Token thresholds are in whole tokens (not atoms).
 */
export const CURVE_BANDS = [
  {
    band_id: 1,
    end_tokens: 3000,
    start_price_lamports: 1_000_000,
    slope_lamports_per_token: 400,
  },
  {
    band_id: 2,
    end_tokens: 10_000,
    start_price_lamports: 2_200_000,
    slope_lamports_per_token: 700,
  },
  {
    band_id: 3,
    end_tokens: 20_000,
    start_price_lamports: 7_100_000,
    slope_lamports_per_token: 1200,
  },
  {
    band_id: 4,
    end_tokens: Infinity,
    start_price_lamports: 19_100_000,
    slope_lamports_per_token: 1800,
  },
];

/**
 * Convert token atoms to whole tokens
 */
export function atomsToTokens(atoms) {
  return Number(atoms) / TOKEN_SCALE;
}

/**
 * Convert whole tokens to token atoms
 */
export function tokensToAtoms(tokens) {
  return Math.floor(tokens * TOKEN_SCALE);
}

/**
 * Convert lamports to SOL
 */
export function lamportsToSol(lamports) {
  return lamports / LAMPORTS_PER_SOL;
}

/**
 * Convert SOL to lamports
 */
export function solToLamports(sol) {
  return Math.floor(sol * LAMPORTS_PER_SOL);
}

/**
 * Find which band a given token supply falls into
 */
export function findBandForSupply(supplyTokens) {
  for (let i = 0; i < CURVE_BANDS.length; i++) {
    if (supplyTokens <= CURVE_BANDS[i].end_tokens) {
      return CURVE_BANDS[i];
    }
  }
  return CURVE_BANDS[CURVE_BANDS.length - 1];
}

/**
 * Get the starting token threshold for a band
 */
export function getBandStartTokens(band) {
  const bandIndex = CURVE_BANDS.findIndex((b) => b.band_id === band.band_id);
  if (bandIndex === 0) {
    return 0;
  }
  return CURVE_BANDS[bandIndex - 1].end_tokens;
}

/**
 * Calculate base marginal price (without boost) at a given supply
 * 
 * @param {number} supplyTokens - Current supply in whole tokens
 * @returns {number} Base marginal price in lamports
 */
export function calculateBaseMarginalPrice(supplyTokens) {
  const band = findBandForSupply(supplyTokens);
  const bandStartTokens = getBandStartTokens(band);
  const tokensInBand = supplyTokens - bandStartTokens;
  
  return band.start_price_lamports + (tokensInBand * band.slope_lamports_per_token);
}

/**
 * Calculate boosted marginal price at a given supply
 * 
 * @param {number} supplyTokens - Current supply in whole tokens
 * @param {number} boostLamports - Current boost in lamports
 * @returns {number} Boosted marginal price in lamports
 */
export function calculateBoostedMarginalPrice(supplyTokens, boostLamports) {
  const basePrice = calculateBaseMarginalPrice(supplyTokens);
  return basePrice + boostLamports;
}

/**
 * Generate a series of points for plotting the base curve
 * 
 * @param {number} minTokens - Minimum token supply
 * @param {number} maxTokens - Maximum token supply
 * @param {number} steps - Number of points to generate
 * @returns {Array<{x: number, y: number}>} Array of {x: tokens, y: price_lamports}
 */
export function generateBaseCurvePoints(minTokens, maxTokens, steps = 100) {
  const points = [];
  const stepSize = (maxTokens - minTokens) / (steps - 1);
  
  for (let i = 0; i < steps; i++) {
    const tokens = minTokens + (i * stepSize);
    const price = calculateBaseMarginalPrice(tokens);
    points.push({ x: tokens, y: price });
  }
  
  return points;
}

/**
 * Generate a series of points for plotting the boosted curve
 * 
 * @param {number} minTokens - Minimum token supply
 * @param {number} maxTokens - Maximum token supply
 * @param {number} boostLamports - Current boost in lamports
 * @param {number} steps - Number of points to generate
 * @returns {Array<{x: number, y: number}>} Array of {x: tokens, y: price_lamports}
 */
export function generateBoostedCurvePoints(minTokens, maxTokens, boostLamports, steps = 100) {
  const points = [];
  const stepSize = (maxTokens - minTokens) / (steps - 1);
  
  for (let i = 0; i < steps; i++) {
    const tokens = minTokens + (i * stepSize);
    const price = calculateBoostedMarginalPrice(tokens, boostLamports);
    points.push({ x: tokens, y: price });
  }
  
  return points;
}

/**
 * Calculate a reasonable visible range for the chart based on current supply
 * 
 * @param {number} currentSupplyTokens - Current supply in whole tokens
 * @returns {{minTokens: number, maxTokens: number}} Visible range
 */
export function calculateVisibleRange(currentSupplyTokens) {
  // Always show multiple bands - start with a wide view
  if (currentSupplyTokens <= 0) {
    return { minTokens: 0, maxTokens: 15_000 };
  }
  
  // Early round (0-3000 tokens): show first 3 bands
  if (currentSupplyTokens < 3000) {
    const minTokens = 0;
    const maxTokens = Math.max(15_000, currentSupplyTokens + 10_000);
    return { minTokens, maxTokens };
  }
  
  // Mid round (3000-10000 tokens): show bands around current position
  if (currentSupplyTokens < 10_000) {
    const minTokens = Math.max(0, currentSupplyTokens - 5000);
    const maxTokens = Math.min(25_000, currentSupplyTokens + 15_000);
    return { minTokens, maxTokens };
  }
  
  // Late round (10000+ tokens): wide zoom
  const minTokens = Math.max(0, currentSupplyTokens - 8000);
  const maxTokens = currentSupplyTokens + 20_000;
  return { minTokens, maxTokens };
}

/**
 * Get band transition points (for visual guides)
 * 
 * @returns {Array<{tokens: number, label: string}>} Band transitions
 */
export function getBandTransitions() {
  return CURVE_BANDS.slice(0, -1).map((band) => ({
    tokens: band.end_tokens,
    label: `Band ${band.band_id + 1}`,
  }));
}
